import { getSupportSession } from "@/lib/supportSession";

// Tables whose rows are directly owned by a company. In Support Mode every
// PostgREST operation on these tables is constrained at the client boundary,
// even if an individual screen forgets its explicit company filter.
const TENANT_TABLES = new Set([
  "accounting_integrations", "accounting_orphan_recovery", "ai_usage_log",
  "audit_logs", "cancellation_feedback", "company_billing", "company_job_usage",
  "company_kpis_daily", "company_members", "company_settings", "company_storage_daily",
  "consolidated_invoices", "customers", "device_tokens", "driver_location_history",
  "driver_messages", "drivers", "expenses", "feedback_email_log", "feedback_requests",
  "invoice_sync_queue", "job_cancellation_logs", "job_deletion_logs", "jobs",
  "notifications", "outbox_tasks", "page_view_logs", "payment_sync_log",
  "qb_invoice_retry_queue", "qb_invoice_sync_attempts", "qb_invoice_sync_runs",
  "qb_orphan_recovery", "quickbooks_api_logs", "quotes", "rate_limits",
  "scheduled_account_cleanups", "security_events_daily", "sms_log", "user_activity_logs",
  "user_feedback", "vehicle_appraisals", "vehicle_daily_checks",
  "vehicle_part_inspections", "vehicle_reminder_log", "vehicles",
]);

const READ_METHODS = new Set(["select"]);
const UPDATE_METHODS = new Set(["update"]);
const INSERT_METHODS = new Set(["insert", "upsert"]);

function withCompany(payload: unknown, companyId: string) {
  if (Array.isArray(payload)) return payload.map((row) => ({ ...(row as object), company_id: companyId }));
  return { ...((payload ?? {}) as object), company_id: companyId };
}

/** Install once. It is completely inert outside an active Support Session. */
export function installSupportTenantGuard(client: any) {
  const originalFrom = client.from.bind(client);
  const originalInvoke = client.functions.invoke.bind(client.functions);

  client.from = (table: string) => {
    const builder = originalFrom(table);
    if (!TENANT_TABLES.has(table)) return builder;

    return new Proxy(builder, {
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);
        if (typeof prop !== "string" || typeof value !== "function") {
          return value;
        }

        if (READ_METHODS.has(prop)) {
          return (...args: unknown[]) => {
            const query = value.apply(target, args);
            const support = getSupportSession();
            return support ? query.eq("company_id", support.companyId) : query;
          };
        }

        if (UPDATE_METHODS.has(prop)) {
          return (payload: unknown, ...args: unknown[]) => {
            const support = getSupportSession();
            const query = value.apply(target, [support ? withCompany(payload, support.companyId) : payload, ...args]);
            return support ? query.eq("company_id", support.companyId) : query;
          };
        }

        if (prop === "delete") {
          return (...args: unknown[]) => {
            const query = value.apply(target, args);
            const support = getSupportSession();
            return support ? query.eq("company_id", support.companyId) : query;
          };
        }

        if (INSERT_METHODS.has(prop)) {
          return (payload: unknown, ...args: unknown[]) => {
            const support = getSupportSession();
            return value.apply(target, [support ? withCompany(payload, support.companyId) : payload, ...args]);
          };
        }

        return value.bind(target);
      },
    });
  };

  // Privileged functions must receive the same effective tenant. Functions
  // that do not use these fields remain unaffected.
  client.functions.invoke = (name: string, options: any = {}) => {
    const support = getSupportSession();
    if (!support) return originalInvoke(name, options);
    return originalInvoke(name, {
      ...options,
      body: {
        ...(options?.body ?? {}),
        company_id: support.companyId,
        companyId: support.companyId,
        support_session_id: support.id,
      },
    });
  };
}